import { IAssistantScript } from '@interfaces/assistantScript';
import { regionNameToCamelCase } from './Regions';

// Newer maps carry a real region flagged isStartingPoint — when present, it
// replaces the synthetic 'landing' pseudo-trigger as the "on join" trigger.
// Falls back to 'landing' for older maps that don't have one.
export const resolveJoinTriggerName = (
  regions?: { name?: unknown; isStartingPoint?: unknown }[] | null,
): string => {
  const startingPointRegion = regions?.find(
    (region) => region?.isStartingPoint === true,
  );
  return typeof startingPointRegion?.name === 'string'
    ? startingPointRegion.name
    : 'landing';
};

// Matches useRegionHandler's script-trigger normalization so map keys/values
// line up directly against SYNC_DATA_UE's incoming region name.
const normalizeRegionName = (value: string): string =>
  value.includes('_') && !value.startsWith('AITrigger_')
    ? regionNameToCamelCase(value)
    : value;

// Maps a child region's normalized name to its parent's normalized name, for
// regions that declare a parentTrigger (e.g. exhibition booths grouped under
// an exhibition hall). A script is often bound to the parent's name only
// ([parentName, ...spots]) — without this map, walking into a child region
// never matches that script and Noa's UI stays hidden.
export const buildParentTriggerMap = (
  regions?:
    | {
        name?: unknown;
        parentTrigger?: { name?: unknown } | string | null;
      }[]
    | null,
): Record<string, string> => {
  const map: Record<string, string> = {};
  if (!Array.isArray(regions)) {
    return map;
  }
  for (const region of regions) {
    if (typeof region?.name !== 'string' || !region.name) {
      continue;
    }
    const { parentTrigger } = region;
    const parentName =
      typeof parentTrigger === 'string'
        ? parentTrigger
        : typeof parentTrigger?.name === 'string'
          ? parentTrigger.name
          : undefined;
    if (parentName) {
      map[normalizeRegionName(region.name)] = normalizeRegionName(parentName);
    }
  }
  return map;
};

export const isLandingTrigger = (
  scriptTrigger: string | string[],
  joinTriggerName = 'landing',
): boolean => {
  const triggers = Array.isArray(scriptTrigger)
    ? scriptTrigger
    : [scriptTrigger];
  const target = joinTriggerName.toLowerCase();
  return triggers.some((t) => t.toLowerCase() === target);
};

export const isAITrigger = (scriptTrigger: string | string[]): boolean => {
  const triggers = Array.isArray(scriptTrigger)
    ? scriptTrigger
    : [scriptTrigger];
  return triggers.some((t) => t.startsWith('AITrigger_'));
};

// Finds any script (regardless of triggerType) bound to the given region,
// using the same trigger-array normalization as useRegionHandler's local
// findScriptForRegion closure — so a script authored with an underscored
// region token (e.g. SpotRegion_Lobby) still matches the camelCased name
// SYNC_DATA_UE reports.
export const findScriptForRegion = (
  scripts: IAssistantScript[],
  regionName?: string,
): IAssistantScript | undefined => {
  if (!regionName) {
    return undefined;
  }

  return scripts.find((script) => {
    const triggers = Array.isArray(script.scriptTrigger)
      ? script.scriptTrigger
      : [script.scriptTrigger];
    return triggers.map(normalizeRegionName).includes(regionName);
  });
};

// Finds the ui_click script (if any) bound to the given region, using the
// same trigger-array normalization as useRegionHandler's findScriptForRegion
// — so a script authored with an underscored region token (e.g.
// SpotRegion_Lobby) still matches the camelCased name SYNC_DATA_UE reports.
export const findUiClickScriptForRegion = (
  scripts: IAssistantScript[],
  regionName?: string,
): IAssistantScript | undefined => {
  if (!regionName) {
    return undefined;
  }

  return scripts.find((script) => {
    if (script.triggerType !== 'ui_click') {
      return false;
    }

    const triggers = Array.isArray(script.scriptTrigger)
      ? script.scriptTrigger
      : [script.scriptTrigger];
    return triggers.map(normalizeRegionName).includes(regionName);
  });
};
